import {
    Box, Button, CloseButton, Code, Divider, Flex, Input, Table, Tbody, Td,
    Text, Th, Thead, Tr, useColorModeValue
} from "@chakra-ui/react";
import { useState } from "react";
import { translate, TranslationKeys } from "../../../i18n";
import {
    calculateEloAdjustment, formatAdjustment, parseNum, computeRoundsFromSets,
    EloCalcInput, EloConfig, SetScore,
} from "../utils/elo.utils";

type EloSimulatorProps = EloConfig;

type PresetScenario = EloCalcInput & { labelKey: TranslationKeys };

const SCENARIOS: PresetScenario[] = [
    { labelKey: 'organization.elo_scenario_equal_win', playerRating: 5.0, opponentRating: 5.0, result: 'win', roundsWon: 6, roundsLost: 4 },
    { labelKey: 'organization.elo_scenario_upset_win', playerRating: 3.0, opponentRating: 7.0, result: 'win', roundsWon: 7, roundsLost: 5 },
    { labelKey: 'organization.elo_scenario_expected_win', playerRating: 7.0, opponentRating: 3.0, result: 'win', roundsWon: 6, roundsLost: 2 },
    { labelKey: 'organization.elo_scenario_equal_draw', playerRating: 5.0, opponentRating: 5.0, result: 'draw' },
    { labelKey: 'organization.elo_scenario_upset_draw', playerRating: 3.0, opponentRating: 7.0, result: 'draw' },
];

export function EloFormula({ kFactor, scalingFactor, marginOfVictoryEnabled, maxLevelAdjustment }: EloConfig) {
    const K = kFactor;
    const S = scalingFactor;
    const max = maxLevelAdjustment;
    const formulaBg = useColorModeValue('gray.50', 'whiteAlpha.100');

    return (
        <Box mb={3}>
            <Text fontWeight="bold" fontSize="sm" mb={1}>{translate('organization.elo_formula_title')}</Text>
            <Box
                bg={formulaBg}
                borderRadius="md"
                px={3}
                py={2}
                fontSize="xs"
                fontFamily="mono"
                lineHeight="base"
            >
                <Text as="span" color="gray.500">{translate('organization.elo_formula_expected')}: </Text>
                <Code bg="transparent" fontSize="xs">E = 1 / (1 + 10^((opp - player) / {S}))</Code>
                <br />
                <Text as="span" color="gray.500">{translate('organization.elo_formula_adjustment')}: </Text>
                <Code bg="transparent" fontSize="xs">adj = {K} × (result - E)</Code>
                {marginOfVictoryEnabled && (
                    <>
                        <br />
                        <Text as="span" color="gray.500">{translate('organization.elo_formula_margin')}: </Text>
                        <Code bg="transparent" fontSize="xs">adj × (1 + 0.5 × |diff| / total)</Code>
                    </>
                )}
                <br />
                <Text as="span" color="gray.500">{translate('organization.elo_formula_clamped')}: </Text>
                <Code bg="transparent" fontSize="xs">clamp(adj, -{max}, +{max})</Code>
            </Box>
        </Box>
    );
}

export default function EloSimulator(props: EloSimulatorProps) {
    const [playerLevel, setPlayerLevel] = useState('5.0');
    const [opponentLevel, setOpponentLevel] = useState('5.0');
    const [sets, setSets] = useState<SetScore[]>([
        { player: '6', opponent: '4' },
    ]);

    const playerRating = parseNum(playerLevel, 1);
    const opponentRating = parseNum(opponentLevel, 1);
    const { roundsWon, roundsLost } = computeRoundsFromSets(sets);

    const customWin = calculateEloAdjustment(
        { playerRating, opponentRating, result: 'win', roundsWon, roundsLost },
        props,
    );
    const customLoss = calculateEloAdjustment(
        { playerRating, opponentRating, result: 'loss', roundsWon: roundsLost, roundsLost: roundsWon },
        props,
    );
    const customDraw = calculateEloAdjustment(
        { playerRating, opponentRating, result: 'draw' },
        props,
    );

    const updateSet = (index: number, field: 'player' | 'opponent', value: string) => {
        setSets(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
    };

    const addSet = () => {
        setSets(prev => [...prev, { player: '0', opponent: '0' }]);
    };

    const removeSet = (index: number) => {
        setSets(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Box>
            <Text fontWeight="bold" mb={2}>{translate('organization.elo_simulator_title')}</Text>
            <Table size="sm" variant="simple">
                <Thead>
                    <Tr>
                        <Th>{translate('organization.elo_scenario')}</Th>
                        <Th isNumeric>{translate('organization.elo_winner_gets')}</Th>
                        <Th isNumeric>{translate('organization.elo_loser_gets')}</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {SCENARIOS.map((scenario) => {
                        const adj = calculateEloAdjustment(scenario, props);
                        return (
                            <Tr key={scenario.labelKey}>
                                <Td>{translate(scenario.labelKey)}</Td>
                                <Td isNumeric>{formatAdjustment(adj)}</Td>
                                <Td isNumeric>{formatAdjustment(-adj)}</Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            <Divider my={4} />

            <Text fontWeight="bold" mb={3}>{translate('organization.elo_custom_title')}</Text>
            <Flex gap={3} mb={3} flexWrap="wrap" align="flex-end">
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>{translate('organization.elo_player_level')}</Text>
                    <Input
                        size="sm"
                        w="70px"
                        value={playerLevel}
                        onChange={(e) => setPlayerLevel(e.target.value)}
                    />
                </Box>
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>{translate('organization.elo_opponent_level')}</Text>
                    <Input
                        size="sm"
                        w="70px"
                        value={opponentLevel}
                        onChange={(e) => setOpponentLevel(e.target.value)}
                    />
                </Box>
            </Flex>

            <Box mb={3}>
                <Text fontSize="xs" color="gray.500" mb={1}>{translate('organization.elo_sets')}</Text>
                <Flex direction="column" gap={2}>
                    {sets.map((set, i) => (
                        <Flex key={i} align="center" gap={1}>
                            <Input
                                size="sm"
                                w="50px"
                                textAlign="center"
                                value={set.player}
                                onChange={(e) => updateSet(i, 'player', e.target.value)}
                            />
                            <Text fontSize="sm" fontWeight="bold">-</Text>
                            <Input
                                size="sm"
                                w="50px"
                                textAlign="center"
                                value={set.opponent}
                                onChange={(e) => updateSet(i, 'opponent', e.target.value)}
                            />
                            {sets.length > 1 && (
                                <CloseButton size="sm" onClick={() => removeSet(i)} />
                            )}
                        </Flex>
                    ))}
                    <Button size="xs" variant="ghost" onClick={addSet} w="fit-content">
                        + {translate('organization.elo_add_set')}
                    </Button>
                </Flex>
            </Box>

            <Table size="sm" variant="simple">
                <Tbody>
                    <Tr>
                        <Td fontWeight="medium">{translate('organization.elo_result_if_win')}</Td>
                        <Td isNumeric color="green.500" fontWeight="bold">{formatAdjustment(customWin)}</Td>
                    </Tr>
                    <Tr>
                        <Td fontWeight="medium">{translate('organization.elo_result_if_loss')}</Td>
                        <Td isNumeric color="red.500" fontWeight="bold">{formatAdjustment(customLoss)}</Td>
                    </Tr>
                    <Tr>
                        <Td fontWeight="medium">{translate('organization.elo_result_if_draw')}</Td>
                        <Td isNumeric fontWeight="bold">{formatAdjustment(customDraw)}</Td>
                    </Tr>
                </Tbody>
            </Table>
        </Box>
    );
}
